import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { _config } from '../Constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlay, faCircleStop, faSpinner } from '@fortawesome/free-solid-svg-icons';
import TranslateIcon from './icons/TranslateIcon';
// Sentence feedback (thumbs up/down) temporarily disabled — bring back later.
// import { faThumbsUp, faThumbsDown } from '@fortawesome/free-regular-svg-icons';

import { trackSentenceFeedback, trackUserSentenceFeedback, trackEvent } from '../utilities/tracking';
import { getFirestoreDocId, getCurrentUserId, saveToUserLibrary } from '../utilities/firebase'; // Added getCurrentUserId and saveToUserLibrary

import { getUrlParameter, isLiteMode } from '../utilities/urlUtils';
import { getTargetLanguageText } from '../utilities/targetLanguageTranslations';
import Typewriter from './Typewriter';
import WordReveal from './WordReveal';
import Loader from './Loader/Loader';
import { getLastUserMessage, updateMessageInHistory } from '../utilities/conversationUtils';
import toast from 'react-simple-toasts';
import SaveSelectionBanner from './SaveSelectionBanner'; // Import the new component
import TypingIndicator from './TypingIndicator'; // Import typing indicator component
import { getLastMessageIndexByRole, scrollToLastConversationItem } from '../utilities/conversationUtils';
import { isLearningActivity, segmentConversationText } from '../utilities/conversationProgress';
import { STOP_PLAYBACK_EVENT, requestPlaybackStop } from '../utilities/playbackControl';
import { isMessageAudioPlaying } from '../utilities/messagePlayback';

import './ConversationComponent.css';

const ConversationComponent = ({ chatHistory: initialChatHistory = [], sharedState = {}, handlePlayClick, handleStopClick = () => { }, setSharedState }) => {
  const [translations, setTranslations] = useState({});
  const [translationLoading, setTranslationLoading] = useState({});
  const [hoveredMessageIndex, setHoveredMessageIndex] = useState(null);
  const [feedbackGiven, setFeedbackGiven] = useState({});
  const [localChatHistory, setLocalChatHistory] = useState(initialChatHistory);
  const [currentPlayingIndex, setCurrentPlayingIndex] = useState(null);
  // True when the browser blocked automatic playback (iOS/iPad Safari autoplay policy).
  // Drives a visible "tap to hear" affordance so the user knows to tap play.
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const audioRef = useRef(null);
  // Track chat history length when loading started to detect when response arrives
  const chatLengthWhenLoadingStartedRef = useRef(null);

  const [correctionInput, setCorrectionInput] = useState('');
  const [showCorrectionPrompt, setShowCorrectionPrompt] = useState(false);
  const [pendingFeedback, setPendingFeedback] = useState(null);

  // State for the top banner
  const [showSaveBanner, setShowSaveBanner] = useState(false);
  const [bannerSelectedText, setBannerSelectedText] = useState('');
  const [bannerMessageContext, setBannerMessageContext] = useState(''); // To store context for the banner
  const conversationRef = useRef(null); // Ref for the main conversation container

  const { t } = useTranslation();
  const selectedActivity = sharedState.selectedActivity || localStorage.getItem('selectedActivity');
  const showLearningHighlights = isLearningActivity(selectedActivity);
  const renderHighlightedText = (text, role) => segmentConversationText(
    text,
    role,
    sharedState.conversationProgress
  ).map((segment, segmentIndex) => (
    segment.type ? (
      <span
        key={`${segmentIndex}-${segment.text}`}
        className={`conversation-highlight conversation-highlight--${segment.type}`}
        title={segment.tooltip}
        aria-label={`${segment.text}. ${segment.tooltip}`}
        tabIndex="0"
      >
        {segment.text}
      </span>
    ) : <React.Fragment key={`${segmentIndex}-${segment.text}`}>{segment.text}</React.Fragment>
  ));
  // Define a custom configuration for toasts
  const customToastConfig = {
    position: 'bottom-center', // Changed to bottom-center
    time: 4000, // Duration in ms
    style: {
      backgroundColor: 'rgba(40, 40, 40, 0.9)', // Dark grey, slightly transparent
      color: 'white',
      padding: '12px 20px',
      borderRadius: '6px',
      fontSize: '15px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
      maxWidth: '90%',
      textAlign: 'center',
    },
    // Retain theme for potential icon or slight color variations from the library if supported on top
  };


  const URL = _config.url;
  const HTTP_translate = `${URL}translate`;

  // Removed old handleTextSelection (tooltip based)

  const handleSaveToLibrary = async () => {
    // Uses bannerSelectedText and bannerMessageContext
    if (!bannerSelectedText || !bannerMessageContext) {
      toast(t('error_no_selection_to_save'), { ...customToastConfig }); // Removed theme
      setShowSaveBanner(false);
      return;
    }

    const userId = getCurrentUserId();
    if (!userId) {
      toast(t('error_user_not_found_for_library'), { ...customToastConfig }); // Removed theme
      setShowSaveBanner(false);
      return;
    }

    try {
      const conversationId = getFirestoreDocId();
      if (!conversationId && bannerMessageContext) { // Only warn if context was available
        console.warn('No active conversation ID found. Cannot save vocabulary item with conversationId.');
        toast(t('error_no_conversation_id_for_vocab'), { ...customToastConfig }); // Removed theme
      }
      await saveToUserLibrary(userId, bannerSelectedText, bannerMessageContext, conversationId);
      toast(t('text_saved_to_library_success'), { ...customToastConfig });
    } catch (error) {
      console.error("Error saving to user vocabulary:", error);
      toast(t('error_saving_to_library', { message: error.message }), { ...customToastConfig }); // Removed theme
    } finally {
      setShowSaveBanner(false);
      setBannerSelectedText('');
      setBannerMessageContext('');
    }
  };

  useEffect(() => {
    const handleGlobalSelectionChange = () => {
      if (!conversationRef.current) return;

      // Disable vocabulary saving in activity pages and lite mode
      if (sharedState.activityPageMode || isLiteMode()) return;

      const selection = window.getSelection();
      const selectedText = selection.toString().trim();

      if (selectedText) {
        // Try to find which message bubble the selection is in to get context
        let parentNode = selection.anchorNode;
        let messageBubble;
        let messageContent;

        while (parentNode && parentNode !== conversationRef.current) {
          if (parentNode.classList && parentNode.classList.contains('bubble') && parentNode.classList.contains('speech')) {
            messageBubble = parentNode;
            break;
          }
          parentNode = parentNode.parentNode;
        }

        if (messageBubble) {
          // Assuming the direct text content of the bubble is the full message.
          // This might need adjustment if bubbles have complex internal structures.
          // A more robust way would be to find the corresponding item in localChatHistory
          // if each bubble had a data-index or similar identifier.
          // For now, we'll try to get it from the DOM element if possible or find it.

          // Find the message in localChatHistory that contains the selected text.
          // This is not perfectly robust if messages are identical, but it's a starting point.
          const potentialMessage = localChatHistory.find(msg => msg.content.includes(selectedText) && messageBubble.textContent.includes(msg.content));
          messageContent = potentialMessage ? potentialMessage.content : messageBubble.textContent.trim();


          setBannerSelectedText(selectedText);
          setBannerMessageContext(messageContent || selectedText); // Fallback to selectedText if full context not found
          setShowSaveBanner(true);
        } else {
          // Selection is not within a message bubble of this component
          setShowSaveBanner(false);
        }
      } else {
        setShowSaveBanner(false);
      }
    };

    document.addEventListener('selectionchange', handleGlobalSelectionChange);
    // Also listen for mouseup for cases where selectionchange might not fire as expected or for clearing.
    document.addEventListener('mouseup', handleGlobalSelectionChange);


    return () => {
      document.removeEventListener('selectionchange', handleGlobalSelectionChange);
      document.removeEventListener('mouseup', handleGlobalSelectionChange);
    };
  }, [localChatHistory]); // Add localChatHistory to deps for context finding

  // Handle automatic playback of new messages
  useEffect(() => {
    const playNewMessage = async () => {
      if (sharedState.autoPlayNextMessage && 
          initialChatHistory && 
          initialChatHistory.length > 0) {
          const lastAssistantMessage = initialChatHistory
          .filter(message => message.role === 'assistant')
          .pop();
        if (lastAssistantMessage && lastAssistantMessage.audio) {
          // Never auto-play into an open microphone: if the learner is already
          // recording when the audio arrives (a race the mic-locking should make
          // rare), playing now would transcribe Madame AI's own voice as theirs.
          // Clear the flag without playing — the loading play-button has become a
          // normal play control they can tap after their recording.
          if (sharedState.record) {
            setSharedState(prevState => ({ ...prevState, autoPlayNextMessage: false }));
            return;
          }
          setSharedState(prevState => ({
            ...prevState,
            autoPlayNextMessage: false
          }));
          handleMessagePlay(getLastMessageIndexByRole(initialChatHistory, "assistant"), lastAssistantMessage.audio);
        }
      }
    };

    if (initialChatHistory && initialChatHistory.length > 0) {
      console.log('Chat history updated:', initialChatHistory);
      setLocalChatHistory(initialChatHistory);
      playNewMessage().catch(error => {
        console.error('Error during automatic playback:', error);
      });
    }
  }, [initialChatHistory, sharedState.autoPlayNextMessage, sharedState.record, setSharedState]); // Added setSharedState

  const storeUserFeedback = async ({
    audio_url,
    detected_text,
    corrected_text,
    mother_tongue,
    level,
    positive,
    target_language
  }) => {
    try {
      await axios.post(`${URL}userFeedback`,
        {
          audio_url: audio_url || '',
          detected_text: detected_text || '',
          corrected_text: corrected_text || '',
          mother_tongue: mother_tongue || '',
          level: level || '',
          positive: positive,
          target_language: target_language || ''
        }
      );
      console.log('storeUserFeedback: Done');
    } catch (err) {
      console.error('Failed to store feedback:', err);
    }
  };

  const [pendingPlayRequest, setPendingPlayRequest] = useState(null);
  // Handles the playback of audio messages.
  // It uses the audio URL to play the audio for a specific message,
  // managing user interactions with play and stop controls.
  // It also handles the automatic playback of the next message.
  const handleMessagePlay = (index, audioUrl) => {
    
    if (!audioRef.current) {
      // Cache the play request for when the audio element is ready
      setPendingPlayRequest({ index, audioUrl });
      return;
    }
  
    // Stop any currently playing audio first
    if (currentPlayingIndex !== null) {
      handleMessageStop();
    }
    
    console.log('handleMessagePlay: Setting currentPlayingIndex', index);
    // Set the current playing index
    setCurrentPlayingIndex(index);
    
    // Play the audio for this specific message
    if (audioRef.current) {
      console.log('handleMessagePlay: audioRef.current', audioRef.current);
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = audioUrl;
      audioRef.current.load();
      
      setSharedState(prevState => ({
        ...prevState,
        loading: true
      }));
      
      audioRef.current.play()
        .then(() => {
          console.log('handleMessagePlay: audioRef.current.play()');

          // Playback succeeded (this also "unlocks" the element on iOS once it
          // happens within a user gesture), so clear any tap-to-hear prompt.
          setAutoplayBlocked(false);
          setSharedState(prevState => ({
            ...prevState,
            talking: true,
            loading: true // Keep loading true while audio is playing
          }));
        })
        .catch(error => {
          console.log('handleMessagePlay: audioRef.current.play() error');

          console.error('Error playing audio:', error);
          handleMessageStop();

          // iOS/iPad Safari rejects programmatic play() that isn't triggered directly
          // by a user gesture (NotAllowedError). The audio is fine — the user just has
          // to tap play. Surface a clear affordance instead of failing silently.
          if (error && error.name === 'NotAllowedError') {
            setAutoplayBlocked(true);
            toast(t('tap_to_hear_hint', { defaultValue: 'Tap ▶ to hear the response' }), customToastConfig);
          }
        });
    }
  };
  useEffect(() => {
    if (pendingPlayRequest && audioRef.current) {
      // Play the pending audio now that the ref is ready
      handleMessagePlay(pendingPlayRequest.index, pendingPlayRequest.audioUrl);
      setPendingPlayRequest(null); // Clear the pending request
    }
  }, [pendingPlayRequest, audioRef.current]);

  const handleMessageStop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.removeAttribute('src');
      audioRef.current.load();
    }
    setPendingPlayRequest(null);
    setAutoplayBlocked(false);
    setCurrentPlayingIndex(null);
    setSharedState(prevState => ({
      ...prevState,
      talking: false,
      loading: false // Reset loading state only when audio stops
    }));
  }, [setSharedState]);

  useEffect(() => {
    const handleStopPlayback = () => {
      handleMessageStop();
    };

    window.addEventListener(STOP_PLAYBACK_EVENT, handleStopPlayback);
    return () => window.removeEventListener(STOP_PLAYBACK_EVENT, handleStopPlayback);
  }, [handleMessageStop]);

  const regenerateWithCorrection = async (updatedChatHistory, userMsgIndex, correctionInput) => {
    const conversationId = getFirestoreDocId();

    const requestData = {
      conversationId,
      messageIndex: userMsgIndex,
      previousHistory: updatedChatHistory.slice(0, userMsgIndex + 1), // up to and including the corrected message
      level: localStorage.getItem('userLevel') || 'unknown',
      age: localStorage.getItem('userAge') || '20',
      language: localStorage.getItem('userLanguage') || 'en',
      activity: localStorage.getItem('selectedActivity') || null,
      custom_activity: getUrlParameter('custom_activity') || '',
      custom_prompt: localStorage.getItem('customPrompt') || '',
      transcriptionText: correctionInput,
      is_regeneration: true,
      languageVariant: localStorage.getItem('languageVariant') || 'france' //  Language variant preference
    };
  
    const response = await axios.post(`${_config.url}regenerate`, requestData);
    return response.data; // contains new text and audio
  };

  // Handle audio ended event
  useEffect(() => {
    const audio = audioRef.current;
    
    const handleAudioEnded = () => {
      handleMessageStop(); // This will reset both talking and loading states
    };

    if (audio) {
      audio.addEventListener('ended', handleAudioEnded);
    }

    return () => {
      if (audio) {
        audio.removeEventListener('ended', handleAudioEnded);
      }
    };
  }, [handleMessageStop]);

  const handleTranslate = async (text, index) => {
    const targetLanguage = sharedState.userLanguage || 'en';

    // Set loading state for this specific message
    setTranslationLoading(prev => ({ ...prev, [index]: true }));

    try {
      const response = await axios.post(HTTP_translate, { text, targetLanguage }, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      console.log('translated data:', response.data.translatedText);

      setTranslations(prevTranslations => ({
        ...prevTranslations,
        [index]: response.data.translatedText,
      }));

    } catch (error) {
      console.error('Error translating text:', error);
      // Show error to user
      const errorMessage = error?.message || 'Translation failed. Please try again.';
      toast(errorMessage, { ...customToastConfig });
    } finally {
      // Clear loading state
      setTranslationLoading(prev => ({ ...prev, [index]: false }));
    }
  };

  const handleFeedback = async (index, isPositive) => {
    if (feedbackGiven[index]) return;
    
    const message = localChatHistory[index];
    const conversationId = getFirestoreDocId();
    
    // Use different tracking functions based on message role
    if (message.role === 'user') {
      const userAudioUrl = message?.audio || null;
      
      trackUserSentenceFeedback(
        conversationId,
        message.content,
        index,
        isPositive,
        userAudioUrl
      );
    } else {
      trackSentenceFeedback(
        conversationId,
        message.content,
        index,
        isPositive
      );
    }

    setFeedbackGiven(prev => ({
      ...prev,
      [index]: true
    }));

    if (isPositive && message.role === 'assistant') {
      toast(t('feedback_sent'));
    }
    
    // Only proceed with regeneration if it's an assistant message and feedback is negative
    else if (!isPositive && message.role === 'assistant') {
      setIsLoading(true); // Show spinner
      try {
        // Stop any currently playing audio before regeneration
        if (currentPlayingIndex !== null) {
          handleMessageStop();
        }

        const historyUpToIndex = localChatHistory.slice(0, index + 1);
        const lastUserMessage = getLastUserMessage(historyUpToIndex);
        
        if (!lastUserMessage) {
          console.error('No user message found in history');
          return;
        }

        const requestData = {
          conversationId: getFirestoreDocId() || null,
          messageIndex: index,
          previousHistory: historyUpToIndex,
          level: localStorage.getItem('userLevel') || 'unknown',
          age: localStorage.getItem('userAge') || '20',
          language: localStorage.getItem('userLanguage') || 'en',
          activity: localStorage.getItem('selectedActivity') || null,
          custom_activity: getUrlParameter('custom_activity') || '',
          custom_prompt: localStorage.getItem('customPrompt') || '',
          transcriptionText: lastUserMessage.content,
          is_regeneration: true,
          languageVariant: localStorage.getItem('languageVariant') || 'france' // Language variant preference
        };

        console.log('Sending regeneration request with data:', requestData);

        const response = await axios.post(`${URL}regenerate`, requestData);
        console.log('Received regeneration response:', response.data);

        if (response.data && response.data.text) {
          console.log('Regeneration response:', response.data);
          console.log('New audio URL:', response.data.audio);
          
          // Update the local chat history with the new response
          const updatedHistory = updateMessageInHistory(localChatHistory, index, {
            content: response.data.text,
            audio: response.data.audio
          });
          
          // Update local state first
          setLocalChatHistory(updatedHistory);

          // Update parent state
          setSharedState(prevState => ({
            ...prevState,
            chatHistory: updatedHistory
          }));

          setIsLoading(false); // Hide spinner
          toast(t('feedback_sent'));

          // After states are updated, trigger playback
          setTimeout(() => {
            handleMessagePlay(index, response.data.audio);
          }, 100);
        } else {
          console.error('Invalid response format:', response.data);
          toast(t('error_regenerating') || 'Invalid response format. Please try again.', { ...customToastConfig });
          setIsLoading(false); // Hide spinner
        }
      } catch (error) {
        console.error('Error regenerating response:', error);
        setIsLoading(false); // Hide spinner

        // Show error to user with safe null checks
        const errorMessage = error?.message || t('error_regenerating') || 'Failed to regenerate response. Please try again.';
        toast(errorMessage, { ...customToastConfig });
      }
    } else if (!isPositive && message.role === 'user') {
        // Gather all required fields
        const audioUrl = message.audio || '';
        const motherTongue = localStorage.getItem('userLanguage') || 'unknown';
        const level = localStorage.getItem('userLevel') || 'unknown';

        // Show the correction modal and store the pending feedback info
        setPendingFeedback({
          audio_url: audioUrl,
          detected_text: message.content,
          mother_tongue: motherTongue,
          level: level,
          positive: false,
          target_language: sharedState.targetLanguage || 'French',
          index
        });
        setCorrectionInput(message.content);
        setShowCorrectionPrompt(true);
        return; // Wait for user to submit correction
    } else if (isPositive && message.role === 'user') {
      storeUserFeedback({
        audio_url: message.audio || '',
        detected_text: message.content,
        mother_tongue: localStorage.getItem('userLanguage') || 'unknown',
        level: localStorage.getItem('userLevel') || 'unknown',
        positive: true,
        target_language: sharedState.targetLanguage || 'French'
      });
      toast(t('feedback_sent'));
    }
  };

  useEffect(() => {
    // console.log('useEffect:scrollToLastConversationItem');
    // streamingTurn.assistantText is a dep so live streamed text keeps the newest
    // words in view as deltas arrive, not just on commit.
    const scrollTimeout = setTimeout(() => {
      scrollToLastConversationItem()
    }, 100)

    return () => clearTimeout(scrollTimeout)
  }, [sharedState.chatHistory, sharedState.streamingTurn?.assistantText])

  // Track chat history length when loading starts, and scroll to bottom for typing indicator
  useEffect(() => {
    if (sharedState.loading) {
      // Record the chat history length when loading starts
      // This allows us to detect when new messages arrive (response received)
      if (chatLengthWhenLoadingStartedRef.current === null) {
        chatLengthWhenLoadingStartedRef.current = localChatHistory.length;
      }
      const scrollTimeout = setTimeout(() => {
        scrollToLastConversationItem()
      }, 100)
      return () => clearTimeout(scrollTimeout)
    } else {
      // Reset when loading ends
      chatLengthWhenLoadingStartedRef.current = null;
    }
  }, [sharedState.loading, localChatHistory.length])


  const getInitialText = () => {
    const baseText = sharedState.selectedActivity
      ? ((sharedState.selectedActivity?.startsWith('learning-path') || sharedState.selectedActivity === 'onboarding-intro')
        ? getTargetLanguageText(sharedState.targetLanguage, 'initialInstruction')
        : getTargetLanguageText(sharedState.targetLanguage, 'activityInstruction', { activity: sharedState.selectedActivity }))
      : getTargetLanguageText(sharedState.targetLanguage, 'defaultInstruction');
    return sharedState.isRealtime ? baseText : `${baseText}\nAppuie sur ▣ une fois terminé.`;
  };

  if (
  	sharedState.selectedActivity?.startsWith('learning-path') &&
  	sharedState.loading &&
  	Array.isArray(localChatHistory) &&
  	localChatHistory.length < 2
  ) {
  	return <Loader />
  }
  

  const handleSubmitCorrection = async () => {
    if (!correctionInput.trim()) return;
    setIsLoading(true);
    setShowCorrectionPrompt(false);
    await storeUserFeedback({
      ...pendingFeedback,
      corrected_text: correctionInput
    });

    // 2. Update chat history with corrected text
    const updatedChatHistory = [...localChatHistory];
    const userMsgIndex = pendingFeedback.index;
    updatedChatHistory[userMsgIndex] = {
      ...updatedChatHistory[userMsgIndex],
      content: correctionInput,
    };

    // 3. Track the edit event
    trackEvent('madameai_sentence_edited', { conversation: updatedChatHistory });

    // 4. Regenerate the response
    const regenerateResponse = await regenerateWithCorrection(updatedChatHistory, userMsgIndex, correctionInput);
    const assistantMsgIndex = userMsgIndex + 1;

    // 5. Update the chat history with the new response
    if (regenerateResponse && regenerateResponse.text && regenerateResponse.audio) {
      updatedChatHistory[assistantMsgIndex] = {
        role: "assistant",
        content: regenerateResponse.text,
        audio: regenerateResponse.audio,
      };

      setLocalChatHistory(updatedChatHistory);
      setSharedState(prev => ({
        ...prev,
        chatHistory: updatedChatHistory
      }));
      toast(t('feedback_sent'));
    } else {
      console.error('Invalid regeneration response:', regenerateResponse);
    }

    // 6. Reset state
    setCorrectionInput('');
    setPendingFeedback(null);
    setIsLoading(false);
  };

  return (
    <div className="conversation" ref={conversationRef}>
      <SaveSelectionBanner
        isVisible={showSaveBanner}
        selectedText={bannerSelectedText}
        onSave={handleSaveToLibrary}
        onDismiss={() => setShowSaveBanner(false)}
        t={t}
      />
      {isLoading && (
        <div className="spinner-container">
          <div className="spinner"></div>
        </div>
      )}
      <audio 
        ref={audioRef}
        onEnded={() => handleMessageStop()}
        preload="auto"
      />
      {localChatHistory.length === 0 ? (
        <div className="conversation-item assistant">
          <div style={{ height: "40px" }}></div>
          <p className="bubble speech assistant">
            <Typewriter
              text={getInitialText()}
              delay={10}
            />
            <span className="translated-text">
              {t('initial_text', { targetGreeting: getTargetLanguageText(sharedState.targetLanguage, 'greeting') })}
            </span>
          </p>
        </div>
      ) : (
        localChatHistory.map((item, index) => {
          const isLastAssistantMessage = index === getLastMessageIndexByRole(localChatHistory, 'assistant');
          const isLastUserMessage = index === getLastMessageIndexByRole(localChatHistory, 'user');
          const isErrorMessage = item.content && item.content.startsWith('⚠️');
          const showAudioControls = item.role === 'assistant' && item.audio && isLastAssistantMessage && !isErrorMessage;
          const isCurrentlyPlaying = isMessageAudioPlaying({
            index,
            currentPlayingIndex,
            talking: sharedState.talking,
            isStreamed: item.streamed,
            isLastAssistantMessage,
          });
          // Hide assistant messages entirely when setting is enabled (except error messages)
          const shouldHideAssistant = item.role === 'assistant' && sharedState.hideAssistantText && !isErrorMessage;

          return (
            <div
              key={index}
              className={`conversation-item ${item.role}${shouldHideAssistant ? ' hidden' : ''}`}
              onMouseEnter={() => setHoveredMessageIndex(index)}
              onMouseLeave={() => setHoveredMessageIndex(null)}
              onClick={() => setHoveredMessageIndex(index)}
            >
              {/* Removed onMouseUp from here as selection is handled globally */}
              {/* Non-realtime: the last assistant message shows as soon as its TEXT
                  arrives (word-by-word reveal), with a loading play-button until the
                  audio attaches — it used to hide entirely until audio was ready,
                  which left the learner staring at a typing indicator through the
                  whole TTS wait. WordReveal keys on `text`, so the later audio attach
                  does not restart the animation. */}
              {/* Streamed turns (item.streamed): plain text — it already rendered live
                  and the audio already played through the Web Audio queue. */}
              <>
              <p className={`bubble speech ${item.role} ${isErrorMessage ? 'error' : ''}`}>
                {/* Word reveal: never over highlighted text (highlights need their spans),
                    never for streamed turns (already rendered live), and — text-first —
                    without waiting for the audio to attach. */}
                {!showLearningHighlights && !sharedState.isRealtime && item.role === 'assistant' && isLastAssistantMessage && !isErrorMessage && !item.streamed
                  ? <WordReveal text={item.content} delay={40} />
                  : showLearningHighlights
                    ? renderHighlightedText(item.content, item.role)
                    : item.content
                }
                {translations[index] && (
                  <span className="translated-text">
                    {translations[index]}
                  </span>
                )}
              </p>
              {item.role === "assistant" && (
                <div className="controls">
                  {/* Audio still generating: a loading play-button (same spinner as the
                      translate control) marks the spot until the URL attaches. Never
                      for streamed turns — their audio already played live. */}
                  {!sharedState.isRealtime && isLastAssistantMessage && !item.audio && !isErrorMessage && !item.streamed && (
                    <FontAwesomeIcon
                      icon={faSpinner}
                      spin
                      className="audio-control audio-control--pending"
                      title={t('audio_loading', { defaultValue: 'Preparing audio…' })}
                    />
                  )}
                  {showAudioControls && (
                    !isCurrentlyPlaying ? (
                      <>
                      <FontAwesomeIcon
                        icon={faCirclePlay}
                        onClick={() => handleMessagePlay(index, item.audio)}
                        className={`audio-control${autoplayBlocked ? ' audio-control--attention' : ''}`}
                      />
                      {autoplayBlocked && (
                        <span
                          className="tap-to-hear-hint"
                          onClick={() => handleMessagePlay(index, item.audio)}
                        >
                          {t('tap_to_hear', { defaultValue: 'Tap to hear' })}
                        </span>
                      )}
                      </>
                    ) : (
                      <FontAwesomeIcon
                        icon={faCircleStop}
                        // A streamed reply plays through ttsStream's Web Audio queue, which
                        // only the global stop event silences; its listener here also runs
                        // handleMessageStop to reset `talking`.
                        onClick={currentPlayingIndex === null
                          ? () => requestPlaybackStop('message-stop')
                          : handleMessageStop}
                        className="audio-control"
                      />
                    )
                  )}
                  {!translations[index] && (hoveredMessageIndex === index || translationLoading[index]) && (
                    <button
                      type="button"
                      className="translate-control"
                      onClick={() => !translationLoading[index] && handleTranslate(item.content, index)}
                      disabled={translationLoading[index]}
                      aria-label={t('translate', { defaultValue: 'Translate' })}
                      title={t('translate', { defaultValue: 'Translate' })}
                    >
                      {translationLoading[index]
                        ? <FontAwesomeIcon icon={faSpinner} spin />
                        : <TranslateIcon />
                      }
                    </button>
                  )}
                  {/* Sentence feedback (thumbs up/down) temporarily disabled — bring back later.
                  {!sharedState.isRealtime && isLastAssistantMessage && ((hoveredMessageIndex === index) || feedbackGiven[index]) && (
                    <div className="feedback-controls">
                      <FontAwesomeIcon
                        icon={faThumbsUp}
                        onClick={() => handleFeedback(index, true)}
                        className={feedbackGiven[index] ? 'feedback-given' : ''}
                      />
                      <FontAwesomeIcon
                        icon={faThumbsDown}
                        onClick={() => handleFeedback(index, false)}
                        className={feedbackGiven[index] ? 'feedback-given' : ''}
                      />
                    </div>
                  )}
                  */}
                </div>
              )}
              </>
              {item.role === "user" && (
                <div className="controls">
                  {/* Sentence feedback (thumbs up/down) temporarily disabled — bring back later.
                  {!sharedState.isRealtime && isLastUserMessage && ((hoveredMessageIndex === index) || feedbackGiven[index]) && (
                    <div className="feedback-controls">
                      <FontAwesomeIcon
                        icon={faThumbsUp}
                        onClick={() => handleFeedback(index, true)}
                        className={feedbackGiven[index] ? 'feedback-given' : ''}
                      />
                      <FontAwesomeIcon
                        icon={faThumbsDown}
                        onClick={() => handleFeedback(index, false)}
                        className={feedbackGiven[index] ? 'feedback-given' : ''}
                      />
                    </div>
                  )}
                  */}
                </div>
              )}
            </div>
          );
        })
      )}
      {/* Ephemeral streamed-turn bubbles: rendered from sharedState.streamingTurn while a
          /turn/stream response is in flight, then replaced in the same render by the
          committed chatHistory messages (the commit clears streamingTurn in one batch).
          Markup/classes are identical to committed messages so all bubble styles apply;
          no controls — translate/replay only make sense on a committed turn. */}
      {sharedState.streamingTurn && sharedState.streamingTurn.userText != null && (
        <div className="conversation-item user streaming-live">
          <p className="bubble speech user">{sharedState.streamingTurn.userText}</p>
        </div>
      )}
      {sharedState.streamingTurn && sharedState.streamingTurn.assistantText && (
        <div className={`conversation-item assistant streaming-live${sharedState.hideAssistantText ? ' hidden' : ''}`}>
          <p className="bubble speech assistant">{sharedState.streamingTurn.assistantText}</p>
        </div>
      )}
      {/* Typing indicator - shows when AI is thinking/generating TTS, hides when speaking */}
      {(() => {
        const lastMessage = localChatHistory[localChatHistory.length - 1];
        const lastMessageIsUser = lastMessage?.role === 'user';

        // Last assistant message — its presence bounds the "waiting" state: once the
        // reply TEXT renders (with a loading play-button while TTS generates), the
        // message itself is the progress signal and the indicator must yield.
        const lastAssistantMsg = [...localChatHistory].reverse().find(m => m.role === 'assistant');

        // Note: On the very first render after loading starts, the ref might not be set yet
        // (useEffect runs after render). In that case refNotYetSet is true.
        const refNotYetSet = chatLengthWhenLoadingStartedRef.current === null;
        const waitingForResponse = refNotYetSet ||
          localChatHistory.length === chatLengthWhenLoadingStartedRef.current;

        // Show in non-realtime when:
        //   1. Waiting for AI response (loading, no new messages yet, no audio on last message)
        //   2. OR TTS is generating in background (assistant message has text but no audio)
        // Never show while audio is playing (talking: true)
        // Streamed turn in flight: the ephemeral bubble carries its own lifecycle — show
        // the indicator until the first reply text lands (status 'waiting' or no deltas
        // yet), then the live text itself is the progress signal.
        const showInNonRealtime = !sharedState.isRealtime &&
          !sharedState.talking &&
          (sharedState.streamingTurn
            ? !sharedState.streamingTurn.assistantText
            : (sharedState.loading && waitingForResponse && !lastAssistantMsg?.audio));

        // Realtime mode: show when connected, not speaking, and last message is from user
        const showInRealtime = sharedState.isRealtime &&
          sharedState.conversationStatus === 'connected' &&
          !sharedState.conversationIsSpeaking &&
          lastMessageIsUser;

        return (showInNonRealtime || showInRealtime) ? <TypingIndicator /> : null;
      })()}
      <div className="conversation-end"></div>
      {showCorrectionPrompt && (
        <div className="correction-modal">
          <div className="correction-modal-content">
            <h3>What should the correct sentence be?</h3>
            <input
              type="text"
              value={correctionInput}
              onChange={e => setCorrectionInput(e.target.value)}
              placeholder={pendingFeedback ? pendingFeedback.detected_text : "Enter the correct sentence"}
              autoFocus
            />
            <button
              onClick={handleSubmitCorrection}
            >
              Submit Correction
            </button>            
            <button
              onClick={() => {
                setShowCorrectionPrompt(false);
                setCorrectionInput('');
                setPendingFeedback(null);
                setFeedbackGiven(prev => {
                  if (pendingFeedback && typeof pendingFeedback.index === 'number') {
                    const updated = { ...prev };
                    delete updated[pendingFeedback.index];
                    return updated;
                  }
                  return prev;
                });  
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversationComponent;
